"use client";

import React, { useState } from "react";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { logger } from "@/utils/logger";
import { useCreditsContext } from "@/context/providers/CreditsProvider";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function FeedbackModal({
  isOpen,
  onClose,
  onSuccess,
}: FeedbackModalProps) {
  const { refresh: invalidateCredits, getCurrentBalance } = useCreditsContext();
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Frontend validation that mirrors backend logic
  const validateFeedback = (text: string) => {
    // Sanitize the text like the backend does
    const sanitized = text
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/<[^>]*>/g, "") // Remove all HTML tags
      .trim();

    const errors: string[] = [];

    if (!text.trim()) {
      errors.push("Enter your feedback.");
    } else if (sanitized.length < 50) {
      errors.push("Enter at least 50 characters of feedback.");
    } else if (sanitized.length > 1000) {
      errors.push("Keep your feedback to 1,000 characters or fewer.");
    } else if (!sanitized || sanitized.replace(/\s/g, "").length === 0) {
      errors.push("Enter your feedback before submitting.");
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedLength: sanitized.length,
      originalLength: text.length,
    };
  };

  const feedbackValidation = validateFeedback(feedbackText);

  const handleSubmit = async () => {
    if (!feedbackValidation.isValid) {
      setError(feedbackValidation.errors[0]);
      return;
    }

    setError(null);
    setSubmittingFeedback(true);

    try {
      // Store balance before submission
      const previousBalance = getCurrentBalance();

      // Submit feedback
      const res = await fetch("/api/credits/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ feedback: feedbackText }),
      });

      const json = await res.json();
      if (!res.ok)
        throw new Error(
          json.error || "We couldn’t submit your feedback. Please try again."
        );

      // If credits were rewarded, wait for them to be registered
      if (json.data.rewarded && json.data.bonus > 0) {
        logger.info("Feedback rewarded, waiting for credits to register", {
          component: "FeedbackModal",
          bonus: json.data.bonus,
          previousBalance,
        });

        // Invalidate and wait for credits to refresh
        await invalidateCredits();

        // Wait for the balance to actually update in the cache
        // Poll getCurrentBalance until it reflects the new balance
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        let newBalance = getCurrentBalance();

        while (
          (newBalance === null ||
            previousBalance === null ||
            newBalance < previousBalance + json.data.bonus) &&
          attempts < maxAttempts
        ) {
          await new Promise(resolve => setTimeout(resolve, 100));
          newBalance = getCurrentBalance();
          attempts++;
        }

        const balanceIncreased =
          newBalance !== null &&
          previousBalance !== null &&
          newBalance >= previousBalance + json.data.bonus;

        if (balanceIncreased) {
          logger.info("Credits verified as registered", {
            component: "FeedbackModal",
            previousBalance,
            newBalance,
            bonus: json.data.bonus,
          });
          setShowSuccess(true);
          setFeedbackText("");
        } else {
          // Credits might still be processing, but show success anyway
          logger.warn("Credits not yet visible, but showing success", {
            component: "FeedbackModal",
            previousBalance,
            newBalance,
            bonus: json.data.bonus,
          });
          setShowSuccess(true);
          setFeedbackText("");
        }
      } else {
        // No credits awarded (already received reward)
        setShowSuccess(true);
        setFeedbackText("");
      }
    } catch (err: any) {
      logger.error("Feedback submission failed", {
        component: "FeedbackModal",
        error: err instanceof Error ? err : new Error(String(err)),
      });
      setError(
        err.message || "We couldn’t submit your feedback. Please try again."
      );
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleClose = () => {
    if (!submittingFeedback) {
      setFeedbackText("");
      setError(null);
      setShowSuccess(false);
      onClose();
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setFeedbackText("");
    setError(null);
    onClose();
    onSuccess?.();
  };

  // Calculate sanitized length for display
  const sanitizedLength = feedbackText
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<[^>]*>/g, "")
    .trim().length;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent showCloseButton={true}>
        <DialogHeader>
          <DialogTitle>
            {showSuccess ? "Feedback submitted" : "Submit feedback"}
          </DialogTitle>
        </DialogHeader>
        {showSuccess ? (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="size-8 text-primary-foreground" />
              </div>
              <p className="text-lg text-primary mb-2">
                Thank you for your feedback.
              </p>
              <p className="text-muted-foreground">
                We’ll use your feedback to improve Build-a-Dress.
              </p>
            </div>
            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              <Button onClick={handleSuccessClose}>Close</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground mb-4">
                Tell us what didn&apos;t work or what we could improve. Share
                your feedback to earn 1 credit.
              </p>
              <Label htmlFor="feedback-text">Your feedback</Label>
              <Textarea
                id="feedback-text"
                value={feedbackText}
                onChange={e => {
                  if (e.target.value.length <= 1000) {
                    setFeedbackText(e.target.value);
                    setError(null);
                  }
                }}
                rows={6}
                className="resize-none"
                placeholder="What didn’t work as expected? What would make it better?"
                disabled={submittingFeedback}
              />
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-muted-foreground">
                  {sanitizedLength < 50 ? (
                    <span className="text-warning">
                      {50 - sanitizedLength} more characters needed
                    </span>
                  ) : (
                    <span className="text-success">
                      {sanitizedLength}/1000 characters
                    </span>
                  )}
                </div>
                {feedbackValidation.errors.length > 0 && (
                  <span className="text-xs text-error">
                    {feedbackValidation.errors[0]}
                  </span>
                )}
              </div>
              {error && (
                <div className="mt-2 text-sm text-error bg-error/10 p-2 rounded">
                  {error}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={submittingFeedback}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!feedbackValidation.isValid || submittingFeedback}
              >
                {submittingFeedback ? (
                  <>
                    <LoadingSpinner
                      size="sm"
                      color="white"
                      className="shrink-0"
                    />
                    Submit feedback
                  </>
                ) : (
                  "Submit feedback"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
